import { DataScienceModule } from '../../types';

export const ML_PIPELINE_MODULE: DataScienceModule = {
  id: 'ds-ml-pipeline',
  title: 'Machine Learning Pipeline Development',
  description: 'Build robust, scalable ML pipelines with validation, feature engineering, and model selection.',
  duration: '3 hours',
  difficulty: 'intermediate',
  objectives: [
    'Design end-to-end ML pipelines',
    'Implement cross-validation strategies',
    'Master hyperparameter tuning',
    'Handle deployment considerations'
  ],
  content: `# Machine Learning Pipeline Development

A well-designed ML pipeline ensures reproducible and maintainable solutions.

## Pipeline Components

1. **Data Ingestion**: Automated loading, validation, and quality checks
2. **Preprocessing**: Missing values, outliers, scaling, encoding
3. **Feature Engineering**: Creation, selection, dimensionality reduction
4. **Model Training**: Algorithm selection, hyperparameter optimization
5. **Evaluation**: Performance metrics, validation strategies
6. **Deployment**: Serialization, API development, monitoring

## Cross-Validation Strategies

- **K-Fold**: Split into k parts, train on k-1, validate on 1
- **Stratified K-Fold**: Maintains class distribution in each fold
- **Time Series CV**: Respects temporal order, prevents leakage

## Hyperparameter Optimization

- **Grid Search**: Exhaustive search over parameter combinations
- **Random Search**: Efficiently samples parameter space
- **Bayesian Optimization**: Uses probabilistic model to guide search

## Model Selection

**Metrics**: Accuracy, Precision, Recall, F1, AUC-ROC, MAE, RMSE

**Considerations**: Bias-variance tradeoff, interpretability, computational cost`,
  codeExamples: [
    {
      title: 'Sklearn Pipeline',
      language: 'python',
      code: `from sklearn.pipeline import Pipeline
from sklearn.preprocessing import StandardScaler
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import cross_val_score, GridSearchCV

# Create pipeline
pipeline = Pipeline([
    ('scaler', StandardScaler()),
    ('classifier', RandomForestClassifier(random_state=42))
])

# Cross-validation
scores = cross_val_score(pipeline, X_train, y_train, cv=5)
print(f"CV Accuracy: {scores.mean():.3f} (+/- {scores.std()*2:.3f})")

# Hyperparameter tuning
param_grid = {
    'classifier__n_estimators': [50, 100],
    'classifier__max_depth': [5, 10, None]
}

grid_search = GridSearchCV(pipeline, param_grid, cv=5, scoring='accuracy')
grid_search.fit(X_train, y_train)
print(f"Best params: {grid_search.best_params_}")
print(f"Best score: {grid_search.best_score_:.3f}")`,
      explanation: 'Build a complete ML pipeline with preprocessing, cross-validation, and hyperparameter tuning.'
    }
  ],
  quiz: {
    questions: [
      {
        id: 'pipeline-q1',
        question: 'Why use pipelines in ML?',
        options: [
          'They make code longer',
          'They ensure reproducibility and prevent data leakage',
          'They slow down training',
          'They are only for production'
        ],
        correctAnswer: 1,
        explanation: 'Pipelines ensure reproducibility and prevent data leakage between train/test sets.'
      },
      {
        id: 'pipeline-q2',
        question: 'What is stratified K-fold cross-validation?',
        options: [
          'Random splitting without any structure',
          'K-fold that maintains class distribution in each fold',
          'Only used for regression',
          'Splits data by time'
        ],
        correctAnswer: 1,
        explanation: 'Stratified K-fold ensures each fold has the same class distribution as the full dataset.'
      },
      {
        id: 'pipeline-q3',
        question: 'When is random search preferred over grid search?',
        options: [
          'Never',
          'When the parameter space is large',
          'When you need exact results',
          'When you have unlimited compute'
        ],
        correctAnswer: 1,
        explanation: 'Random search is more efficient for large parameter spaces and often finds good solutions faster.'
      },
      {
        id: 'pipeline-q4',
        question: 'What is data leakage?',
        options: [
          'Losing data during transfer',
          'Using test data information during training',
          'Missing values in data',
          'Slow data loading'
        ],
        correctAnswer: 1,
        explanation: 'Data leakage occurs when information from test data influences training, leading to overly optimistic results.'
      },
      {
        id: 'pipeline-q5',
        question: 'What does the F1 score measure?',
        options: [
          'Model training speed',
          'Harmonic mean of precision and recall',
          'Total accuracy only',
          'Number of features used'
        ],
        correctAnswer: 1,
        explanation: 'F1 score is the harmonic mean of precision and recall, balancing both metrics.'
      }
    ],
    passThreshold: 70
  },
  practice: {
    task: 'Create a simple sklearn pipeline with scaling and classification.',
    starter: `from sklearn.pipeline import Pipeline

def create_pipeline():
    """
    Create a pipeline with StandardScaler and LogisticRegression.
    """
    # Your code here
    pass`,
    solution: `from sklearn.pipeline import Pipeline
from sklearn.preprocessing import StandardScaler
from sklearn.linear_model import LogisticRegression

def create_pipeline():
    return Pipeline([
        ('scaler', StandardScaler()),
        ('classifier', LogisticRegression(random_state=42))
    ])`,
    hints: [
      'Import StandardScaler and LogisticRegression',
      'Use Pipeline with a list of tuples',
      'Each tuple is (name, transformer/estimator)'
    ]
  }
};
